import {GitHubAvatar} from '@github-ui/github-avatar'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {ArrowUpRightIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import type {AgentMention} from '../../../types/agent-mention'
import {ExpandableMarkdown} from '../../expandable-markdown/ExpandableMarkdown'
import styles from './UserMessage.module.css'
import {useAgentInfo} from '@github-ui/copilot-shared-ui/agents/use-agent-info'
import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import {createMarkdownWithAgentLinks} from '../../../utils/markdown-link-utils'
import {useCurrentUser} from '../../../contexts/CurrentUserContext'
import {MessageTextWithHovercards} from './message-text-with-hovercards'

interface UserMessageProps {
  mention?: AgentMention
  isLoading?: boolean
  agentId?: number | null
}

/**
 * UserMention component displays a message triggered by a user comment or review.
 * TODO: adopt shared components like UserMessageLayout
 */
export const UserMessage = ({mention, isLoading = false, agentId}: UserMessageProps) => {
  const {data: agentInfo} = useAgentInfo(agentId)
  const {currentUser} = useCurrentUser()

  // If we don't have a mention, it's either loading or we failed to fetch it
  if (!mention) {
    return isLoading ? <LoadingMessage /> : <ErrorMessage />
  }

  const getAuthorDisplayLogin = () => {
    if (currentUser && mention.author.id === currentUser.id) {
      return 'You'
    }

    return mention.author.display_login
  }

  return (
    <div className={styles.container}>
      <MessageContent
        mention={mention}
        agentInfo={agentInfo ?? undefined}
        authorDisplayLogin={getAuthorDisplayLogin()}
      />
      <div className={styles.avatarContainer}>
        <GitHubAvatar src={mention.author.avatar_url} size={24} />
      </div>
    </div>
  )
}

const ErrorMessage = () => (
  <div className={styles.container}>
    <div className={styles.messageContainer}>
      <p className={styles.messageText}>Failed to fetch triggering comment</p>
    </div>
  </div>
)

const LoadingMessage = () => (
  <div className={styles.container}>
    <div className={styles.messageContainer}>
      <span className={styles.messageMeta}>
        <LoadingSkeleton variant="rounded" height="sm" width="80px" />
      </span>
      <div className={styles.messageText}>
        <LoadingSkeleton variant="rounded" height="sm" width="random" />
      </div>
    </div>
    <div className={styles.avatarContainer}>
      <LoadingSkeleton variant="elliptical" height="24px" width="24px" />
    </div>
  </div>
)

const MessageContent = ({
  mention,
  agentInfo,
  authorDisplayLogin,
}: {
  mention: AgentMention
  agentInfo: AgentInfo | undefined
  authorDisplayLogin: string
}) => {
  const hasBody = !!mention.body

  return (
    <div className={styles.messageContainer}>
      {hasBody && <MessageMeta mention={mention} authorDisplayLogin={authorDisplayLogin} />}
      <MessageText mention={mention} agentInfo={agentInfo} authorDisplayLogin={authorDisplayLogin} />
      {mention.type === 'review' && <ReviewCommentText mention={mention} />}
    </div>
  )
}

const MessageMeta = ({mention, authorDisplayLogin}: {mention: AgentMention; authorDisplayLogin: string}) => {
  const {type, url, path, isPending} = mention

  const getMetaContent = () => {
    switch (type) {
      case 'single_comment_review':
        return (
          <span className={styles.truncatedPath}>
            {authorDisplayLogin} reviewed{' '}
            <Link muted inline href={url}>
              {path}
            </Link>
          </span>
        )

      case 'review':
        return `${authorDisplayLogin} reviewed`

      case 'timeline_comment':
      default:
        if (isPending) {
          return `${authorDisplayLogin} commented`
        }
        return (
          <>
            {authorDisplayLogin}{' '}
            <Link muted inline href={url}>
              commented
            </Link>
          </>
        )
    }
  }

  return <span className={styles.messageMeta}>{getMetaContent()}</span>
}

const MessageText = ({
  mention,
  agentInfo,
  authorDisplayLogin,
}: {
  mention: AgentMention
  agentInfo: AgentInfo | undefined
  authorDisplayLogin: string
}) => {
  if (mention.type === 'review' && !mention.body) {
    return <p className={styles.messageText}>{`${authorDisplayLogin} requested changes`}</p>
  }

  if (!mention.body) {
    return null
  }

  if (agentInfo) {
    return <MessageTextWithHovercards body={mention.body} agentInfo={agentInfo} />
  }

  return (
    <ExpandableMarkdown
      className={styles.messageText}
      markdown={createMarkdownWithAgentLinks(mention.body, agentInfo)}
    />
  )
}

const ReviewCommentText = ({mention}: {mention: AgentMention}) => {
  const commentsCount = mention.child_comments ?? 0
  const commentsText = `${commentsCount} review comment${commentsCount !== 1 ? 's' : ''}`

  return (
    <span className={styles.messageMeta}>
      <Link muted inline href={mention.url}>
        {commentsText}
      </Link>
      <ArrowUpRightIcon size={12} />
    </span>
  )
}
